"use client";

import React, { useEffect, useRef, useState } from "react";
import { MapPin } from "lucide-react";
import { useTranslations } from "next-intl";
import { DESTINATIONS_CONFIG } from "@/app/constants/destinations";

interface Destination {
  name: string;
  image: string;
  isHeader?: boolean;
}

interface HeaderCard {
  title: string;
  description: string;
  button: string;
}

const PlaceSection = () => {
  const t = useTranslations("HomePagePlaceSection");

  const destinations: Destination[] = DESTINATIONS_CONFIG.map(
    ({ key, image }) => ({
      name: t(`destinations.${key}`),
      image,
    })
  );
  const headerCard: HeaderCard = t.raw("headerCard");

  const cardRef = useRef<HTMLDivElement>(null);
  const carouselRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const headerCardIndex = 5;
  const cardWidth = 320;
  const initialOffset = headerCardIndex * cardWidth;
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" && window.innerWidth < 768
  );

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (isMobile) return;

    const carousel = carouselRef.current;
    const container = containerRef.current;
    let currentTranslate = -initialOffset;

    const handleMouseEnter = () => {
      if (carousel) carousel.style.animationPlayState = "paused";
    };

    const handleMouseLeave = () => {
      if (carousel) carousel.style.animationPlayState = "running";
    };

    const handleResize = () => {
      currentTranslate = -initialOffset;
      if (carousel) {
        carousel.style.transition = "none";
        carousel.style.transform = `translateX(${currentTranslate}px)`;
      }
    };

    if (carousel && container) {
      container.addEventListener("mouseenter", handleMouseEnter);
      container.addEventListener("mouseleave", handleMouseLeave);
      window.addEventListener("resize", handleResize);
    }

    return () => {
      if (container) {
        container.removeEventListener("mouseenter", handleMouseEnter);
        container.removeEventListener("mouseleave", handleMouseLeave);
      }
      window.removeEventListener("resize", handleResize);
    };
  }, [isMobile, initialOffset]);

  const handleScrollToCard = () => {
    if (!cardRef.current) return;
    const cardTop =
      cardRef.current.getBoundingClientRect().top + window.pageYOffset;
    const offset = 600;
    window.scrollTo({ top: cardTop - offset, behavior: "smooth" });
  };

  const cards = [...destinations.slice(0, 5), ...destinations.slice(5)];
  const duplicatedCards = [...cards, ...cards];

  return (
    <section
      ref={cardRef}
      className="w-full pt-8 md:pt-16 pb-8 md:pb-16 px-4 bg-white dark:bg-sleap-gray-950 text-sleap-gray-950 dark:text-white m-0"
    >
      <div className="flex flex-col items-center gap-6">
        {isMobile ? (
          <div className="w-full h-[432px] flex-shrink-0">
            <div className="w-full h-[432px] rounded-[18.63px] shadow-rooms-card flex flex-col justify-center gap-8 p-6 pt-4 px-12 bg-sleap-light-blue-50">
              <div
                className="text-left text-3xl font-semibold leading-[38px] font-inter text-sleap-gray-950"
                dangerouslySetInnerHTML={{ __html: headerCard.title }}
              />
              <div className="text-left text-xl font-normal font-inter text-sleap-gray-600">
                {headerCard.description}
              </div>
              <button
                className="w-52 h-12 px-4 py-2 text-white text-sm font-semibold font-inter leading-tight bg-gradient-to-br from-cyan-300 to-violet-500 rounded-lg inline-flex justify-center items-center gap-1 mx-auto mt-3"
                onClick={handleScrollToCard}
              >
                {headerCard.button}
              </button>
            </div>
          </div>
        ) : (
          <div ref={containerRef} className="w-full overflow-hidden relative">
            <div
              ref={carouselRef}
              className="flex gap-5 flex-nowrap slide-left"
              style={{
                width: `${duplicatedCards.length * cardWidth}px`,
                transform: `translateX(-${initialOffset}px)`,
                willChange: "transform",
              }}
            >
              {duplicatedCards.map((card, index) => (
                <div
                  key={index}
                  className="w-[300px] h-[432px] flex-shrink-0 relative transition-opacity duration-300"
                >
                  {card.isHeader ? (
                    <div className="w-[300px] h-[432px] rounded-[18.63px] shadow-rooms-card flex flex-col justify-center gap-8 p-6 pt-4 px-12 bg-sleap-light-blue-50">
                      <div
                        className="text-left text-3xl font-semibold leading-[38px] font-inter text-sleap-gray-950"
                        dangerouslySetInnerHTML={{ __html: headerCard.title }}
                      />
                      <div className="text-left text-xl font-normal font-inter text-sleap-gray-600">
                        {headerCard.description}
                      </div>
                      <button
                        className="w-52 h-12 px-4 py-2 text-white text-sm font-semibold font-inter leading-tight bg-gradient-to-br from-cyan-300 to-violet-500 rounded-lg inline-flex justify-center items-center gap-1 mx-auto mt-3"
                        onClick={handleScrollToCard}
                      >
                        {headerCard.button}
                      </button>
                    </div>
                  ) : (
                    <div className="w-[300px] h-[432px] relative">
                      <img
                        className="w-[300px] h-[432px] rounded-[9.07px] object-cover"
                        src={card?.image ? card.image : ""}
                        alt={`Visit ${card.name}`}
                        loading="lazy"
                      />
                      <div
                        className="absolute left-5 top-[388px] flex items-center gap-2 text-white text-2xl font-semibold font-inter"
                        style={{ textShadow: "0px 2px 4px rgba(0, 0, 0, 0.5)" }}
                      >
                        <MapPin className="w-6 h-6" /> {card.name}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <style jsx global>{`
        @keyframes slideLeft {
          0% {
            transform: translateX(-${initialOffset}px);
          }
          100% {
            transform: translateX(
              -${initialOffset + cards.length * cardWidth}px
            );
          }
        }
        .slide-left {
          animation: slideLeft 60s linear infinite;
          animation-play-state: running;
        }
      `}</style>
    </section>
  );
};

export default PlaceSection;
